import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Spinner,
  Collapse,
} from '@/components/ui-compat'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Building,
  Layers,
  Plus,
  Trash2,
  Settings,
  Pencil,
  Upload,
  Image,
  ChevronDown,
  ChevronRight,
  Check,
  RotateCcw,
  RefreshCw,
  Save,
  LoaderCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import BlueprintUploadModal from './blueprint/BlueprintUploadModal'
import { useJobProgress } from '../../../hooks'

interface BuildingType {
  id: string
  name: string
  code: string | null
  description: string | null
  sortOrder: number
  floors: Floor[]
  floorCount: number
  snagCount: number
  openSnagCount: number
}

interface Floor {
  id: string
  name: string
  level: number
  description: string | null
  sortOrder: number
  blueprintFile: {
    id: string
    filename: string
    key: string
  } | null
  snagCount: number
  openSnagCount: number
}

const SnagsSettings: React.FC = () => {
  const { t } = useTranslation()
  const { data: meData } = useQuery(Query.ME)
  const currentProject = meData?.me?.currentProject
  const projectId = currentProject?.id

  const { canEdit, canAdmin } = useModuleActions('ISSUES', projectId)

  // Building modal state
  const [showBuildingModal, setShowBuildingModal] = useState(false)
  const [editingBuilding, setEditingBuilding] = useState<BuildingType | null>(null)
  const [buildingForm, setBuildingForm] = useState({ name: '', code: '', description: '' })

  // Floor modal state
  const [showFloorModal, setShowFloorModal] = useState(false)
  const [editingFloor, setEditingFloor] = useState<Floor | null>(null)
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | null>(null)
  const [floorForm, setFloorForm] = useState({ name: '', level: '', description: '' })

  // Infrastructure types state
  const [infrastructureTypes, setInfrastructureTypes] = useState<string[]>([])
  const [newInfraType, setNewInfraType] = useState('')
  const [defaultPriority, setDefaultPriority] = useState('MEDIUM')

  // Blueprint state
  const [deletingBlueprintFloorId, setDeletingBlueprintFloorId] = useState<string | null>(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadModalFloorId, setUploadModalFloorId] = useState<string | null>(null)
  const [uploadModalFloorName, setUploadModalFloorName] = useState<string | null>(null)

  // DZI regeneration state
  const [regeneratingFloorId, setRegeneratingFloorId] = useState<string | null>(null)

  // Fetch buildings
  const {
    data: buildingsData,
    loading: buildingsLoading,
    refetch: refetchBuildings,
  } = useQuery(Query.BUILDINGS, {
    variables: { projectId },
    skip: !projectId,
  })

  // Fetch snag settings
  const {
    data: settingsData,
    loading: settingsLoading,
  } = useQuery(Query.SNAG_SETTINGS, {
    variables: { projectId },
    skip: !projectId,
  })

  // Building mutations
  const [createBuilding, { loading: creatingBuilding }] = useMutation(Mutation.CREATE_BUILDING)
  const [updateBuilding, { loading: updatingBuilding }] = useMutation(Mutation.UPDATE_BUILDING)
  const [deleteBuilding] = useMutation(Mutation.DELETE_BUILDING)

  // Floor mutations
  const [createFloor, { loading: creatingFloor }] = useMutation(Mutation.CREATE_FLOOR)
  const [updateFloor, { loading: updatingFloor }] = useMutation(Mutation.UPDATE_FLOOR)
  const [deleteFloor] = useMutation(Mutation.DELETE_FLOOR)
  const [setFloorBlueprint] = useMutation(Mutation.SET_FLOOR_BLUEPRINT)

  // Settings mutation
  const [updateSnagSettings, { loading: savingSettings }] = useMutation(Mutation.UPDATE_SNAG_SETTINGS)

  // DZI regeneration mutation
  const [processBlueprintDzi] = useMutation(Mutation.PROCESS_BLUEPRINT_DZI)

  // Job progress hook for DZI regeneration
  const { job: regenerateJob, startPolling: startRegeneratePolling, stopPolling: stopRegeneratePolling } = useJobProgress({
    onComplete: () => {
      toastr.success('', t('Snags_dzi_regenerated', 'Blueprint tiles regenerated successfully'))
      setRegeneratingFloorId(null)
      if (refetchBuildings) refetchBuildings()
    },
    onError: (error) => {
      toastr.error('', error || t('Snags_dzi_regenerate_failed', 'Failed to regenerate blueprint tiles'))
      setRegeneratingFloorId(null)
    },
  })

  // Initialize settings from data
  useEffect(() => {
    if (settingsData?.snagSettings) {
      setInfrastructureTypes(settingsData.snagSettings.infrastructureTypes || [])
      setDefaultPriority(settingsData.snagSettings.defaultPriority || 'MEDIUM')
    }
  }, [settingsData])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopRegeneratePolling()
    }
  }, [stopRegeneratePolling])

  const buildings: BuildingType[] = buildingsData?.buildings || []

  // Building handlers
  const handleAddBuilding = () => {
    setEditingBuilding(null)
    setBuildingForm({ name: '', code: '', description: '' })
    setShowBuildingModal(true)
  }

  const handleEditBuilding = (building: BuildingType) => {
    setEditingBuilding(building)
    setBuildingForm({
      name: building.name,
      code: building.code || '',
      description: building.description || '',
    })
    setShowBuildingModal(true)
  }

  const handleSaveBuilding = async () => {
    if (!buildingForm.name.trim()) {
      toastr.error('', t('Snags_building_name_required'))
      return
    }

    try {
      if (editingBuilding) {
        await updateBuilding({
          variables: {
            id: editingBuilding.id,
            name: buildingForm.name,
            code: buildingForm.code || null,
            description: buildingForm.description || null,
          },
        })
        toastr.success('', t('Snags_building_updated'))
      } else {
        await createBuilding({
          variables: {
            projectId,
            name: buildingForm.name,
            code: buildingForm.code || null,
            description: buildingForm.description || null,
          },
        })
        toastr.success('', t('Snags_building_created'))
      }
      setShowBuildingModal(false)
      if (refetchBuildings) {
        await refetchBuildings()
      }
    } catch (error: any) {
      toastr.error('', error.message)
    }
  }

  const handleDeleteBuilding = async (building: BuildingType) => {
    if (building.snagCount > 0) {
      if (!window.confirm(t('Snags_delete_building_with_snags', { count: building.snagCount }))) {
        return
      }
    } else if (!window.confirm(t('Snags_delete_building_confirm'))) {
      return
    }

    try {
      await deleteBuilding({
        variables: { id: building.id },
      })
      toastr.success('', t('Snags_building_deleted'))
      if (refetchBuildings) {
        await refetchBuildings()
      }
    } catch (error: any) {
      toastr.error('', error.message)
    }
  }

  // Floor handlers
  const handleAddFloor = (buildingId: string) => {
    setSelectedBuildingId(buildingId)
    setEditingFloor(null)
    setFloorForm({ name: '', level: '', description: '' })
    setShowFloorModal(true)
  }

  const handleEditFloor = (floor: Floor, buildingId: string) => {
    setSelectedBuildingId(buildingId)
    setEditingFloor(floor)
    setFloorForm({
      name: floor.name,
      level: floor.level.toString(),
      description: floor.description || '',
    })
    setShowFloorModal(true)
  }

  const handleSaveFloor = async () => {
    if (!floorForm.name.trim()) {
      toastr.error('', t('Snags_floor_name_required'))
      return
    }

    const level = parseInt(floorForm.level, 10)
    if (isNaN(level)) {
      toastr.error('', t('Snags_floor_level_required'))
      return
    }

    try {
      if (editingFloor) {
        await updateFloor({
          variables: {
            id: editingFloor.id,
            name: floorForm.name,
            level,
            description: floorForm.description || null,
          },
        })
        toastr.success('', t('Snags_floor_updated'))
      } else {
        await createFloor({
          variables: {
            buildingId: selectedBuildingId,
            name: floorForm.name,
            level,
            description: floorForm.description || null,
          },
        })
        toastr.success('', t('Snags_floor_created'))
      }
      setShowFloorModal(false)
      // Refetch buildings to update the list
      if (refetchBuildings) {
        await refetchBuildings()
      }
    } catch (error: any) {
      toastr.error('', error.message)
    }
  }

  const handleDeleteFloor = async (floor: Floor) => {
    if (floor.snagCount > 0) {
      if (!window.confirm(t('Snags_delete_floor_with_snags', { count: floor.snagCount }))) {
        return
      }
    } else if (!window.confirm(t('Snags_delete_floor_confirm'))) {
      return
    }

    try {
      await deleteFloor({
        variables: { id: floor.id },
      })
      toastr.success('', t('Snags_floor_deleted'))
      if (refetchBuildings) {
        await refetchBuildings()
      }
    } catch (error: any) {
      toastr.error('', error.message)
    }
  }

  // Blueprint upload handler - opens modal
  const handleOpenUploadModal = (floor: Floor) => {
    setUploadModalFloorId(floor.id)
    setUploadModalFloorName(floor.name)
    setShowUploadModal(true)
  }

  const handleUploadModalSuccess = async () => {
    setShowUploadModal(false)
    setUploadModalFloorId(null)
    setUploadModalFloorName(null)
    if (refetchBuildings) {
      await refetchBuildings()
    }
  }

  // Blueprint delete handler
  const handleDeleteBlueprint = async (floor: Floor) => {
    if (!floor.blueprintFile) return

    if (!window.confirm(t('Snags_delete_blueprint_confirm_message'))) {
      return
    }

    setDeletingBlueprintFloorId(floor.id)

    try {
      await setFloorBlueprint({
        variables: {
          floorId: floor.id,
          fileId: null,
        },
      })
      toastr.success('', t('Snags_blueprint_deleted'))
      if (refetchBuildings) {
        await refetchBuildings()
      }
    } catch (error: any) {
      console.error('Blueprint delete error:', error)
      toastr.error('', error.message || t('Snags_blueprint_delete_failed'))
    } finally {
      setDeletingBlueprintFloorId(null)
    }
  }

  // DZI regeneration handler
  const handleRegenerateDzi = async (floor: Floor) => {
    if (!floor.blueprintFile) return

    setRegeneratingFloorId(floor.id)

    try {
      const { data: jobData } = await processBlueprintDzi({
        variables: { fileId: floor.blueprintFile.id },
      })

      const jobId = jobData?.processBlueprintDzi?.id
      if (jobId) {
        startRegeneratePolling(jobId)
      } else {
        throw new Error('No job ID returned')
      }
    } catch (error: any) {
      console.error('DZI regeneration error:', error)
      toastr.error('', error.message || t('Snags_dzi_regenerate_failed', 'Failed to start regeneration'))
      setRegeneratingFloorId(null)
    }
  }

  // Infrastructure type handlers
  const handleAddInfraType = () => {
    if (newInfraType.trim() && !infrastructureTypes.includes(newInfraType.trim())) {
      setInfrastructureTypes([...infrastructureTypes, newInfraType.trim()])
      setNewInfraType('')
    }
  }

  const handleRemoveInfraType = (index: number) => {
    setInfrastructureTypes(infrastructureTypes.filter((_, i) => i !== index))
  }

  const handleResetInfraTypes = () => {
    const defaultTypes = [
      t('Snags_infra_electrical', 'Electrical'),
      t('Snags_infra_plumbing', 'Plumbing'),
      t('Snags_infra_hvac', 'HVAC'),
      t('Snags_infra_fire_safety', 'Fire Safety'),
      t('Snags_infra_structural', 'Structural'),
      t('Snags_infra_finishing', 'Finishing'),
      t('Snags_infra_general', 'General'),
    ]
    setInfrastructureTypes(defaultTypes)
  }

  const savedSettings = settingsData?.snagSettings
  const settingsDirty = Boolean(
    savedSettings &&
      (JSON.stringify(infrastructureTypes) !== JSON.stringify(savedSettings.infrastructureTypes || []) ||
        defaultPriority !== (savedSettings.defaultPriority || 'MEDIUM')),
  )

  const handleDiscardSettings = () => {
    setInfrastructureTypes(savedSettings?.infrastructureTypes || [])
    setDefaultPriority(savedSettings?.defaultPriority || 'MEDIUM')
  }

  const handleSaveSettings = async () => {
    try {
      await updateSnagSettings({
        variables: {
          projectId,
          infrastructureTypes,
          defaultPriority,
        },
        refetchQueries: [{ query: Query.SNAG_SETTINGS, variables: { projectId } }],
      })
      toastr.success('', t('Snags_settings_saved'))
    } catch (error: any) {
      toastr.error('', error.message)
    }
  }

  // Track expanded buildings
  const [expandedBuildings, setExpandedBuildings] = useState<Record<string, boolean>>({})

  const toggleBuilding = (buildingId: string) => {
    setExpandedBuildings((prev) => ({ ...prev, [buildingId]: !prev[buildingId] }))
  }

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Buildings & Floors - Main Column */}
        <div className="lg:col-span-2">
          <Card className="mb-4">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 py-4">
              <div className="flex items-center gap-2">
                <Building className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">{t('Snags_buildings_and_floors')}</CardTitle>
              </div>
              {canAdmin && (
                <Button size="sm" onClick={handleAddBuilding}>
                  <Plus className="h-4 w-4" />
                  {t('Snags_add_building')}
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {buildingsLoading ? (
                <div className="text-center py-10">
                  <Spinner color="primary" />
                </div>
              ) : buildings.length === 0 ? (
                <div className="text-center py-10">
                  <Building className="h-10 w-10 text-muted-foreground opacity-30 mx-auto mb-3" />
                  <p className="text-muted-foreground mb-0">{t('Snags_no_buildings')}</p>
                  {canAdmin && (
                    <Button size="sm" className="mt-3" onClick={handleAddBuilding}>
                      <Plus className="h-4 w-4" />
                      {t('Snags_add_first_building', 'Add your first building')}
                    </Button>
                  )}
                </div>
              ) : (
                <div>
                  {buildings.map((building, index) => (
                    <div key={building.id} className={index > 0 ? 'border-t' : ''}>
                      {/* Building Header - Clickable */}
                      <div
                        className="flex items-center justify-between p-3 bg-muted cursor-pointer"
                        onClick={() => toggleBuilding(building.id)}
                      >
                        <div className="flex items-center gap-3">
                          {expandedBuildings[building.id] ? (
                            <ChevronDown className="h-3 w-3 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-3 w-3 text-muted-foreground" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              {building.code && (
                                <Badge variant="secondary" className="text-xs">
                                  {building.code}
                                </Badge>
                              )}
                              <strong>{building.name}</strong>
                            </div>
                            <small className="text-muted-foreground">
                              {building.floorCount} {t('Snags_floors_label', 'floors')}
                              {building.snagCount > 0 && (
                                <span className="ml-2">
                                  | {building.snagCount} {t('Snags_snags_label', 'snags')}
                                  {building.openSnagCount > 0 && (
                                    <Badge variant="destructive" className="ml-1 text-[0.65rem] px-1.5 py-0">
                                      {building.openSnagCount}
                                    </Badge>
                                  )}
                                </span>
                              )}
                            </small>
                          </div>
                        </div>
                        {canAdmin && (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleAddFloor(building.id)}
                              title={t('Snags_add_floor')}
                            >
                              <Layers className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleEditBuilding(building)}
                              title={t('Common_edit')}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteBuilding(building)}
                              title={t('Common_delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Floors List - Collapsible */}
                      <Collapse isOpen={expandedBuildings[building.id]}>
                        {building.floors.length === 0 ? (
                          <div className="text-muted-foreground text-center py-3 px-4 text-sm">
                            {t('Snags_no_floors')}
                          </div>
                        ) : (
                          <div>
                            {building.floors.map((floor) => (
                              <div
                                key={floor.id}
                                className="flex items-center justify-between py-2 px-4 border-b ml-8"
                              >
                                <div className="flex items-center gap-2">
                                  <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                                  <div>
                                    <span className="font-semibold">{floor.name}</span>
                                    <small className="text-muted-foreground ml-2">(L{floor.level})</small>
                                  </div>
                                  {floor.blueprintFile ? (
                                    <Badge variant="default" className="ml-2 text-xs bg-green-600 hover:bg-green-600">
                                      <Image className="h-3 w-3" />
                                      {t('Snags_has_blueprint', 'Blueprint')}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="ml-2 text-xs text-muted-foreground">
                                      {t('Snags_no_blueprint')}
                                    </Badge>
                                  )}
                                  {floor.snagCount > 0 && (
                                    <Badge variant="outline" className="ml-1 text-xs">
                                      {floor.snagCount} {t('Snags_snags_label', 'snags')}
                                      {floor.openSnagCount > 0 && (
                                        <span className="text-destructive ml-1">({floor.openSnagCount})</span>
                                      )}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  {/* Show progress when regenerating */}
                                  {regeneratingFloorId === floor.id ? (
                                    <div className="flex items-center gap-2 mr-2">
                                      <LoaderCircle className="h-4 w-4 animate-spin text-primary" />
                                      <small className="text-muted-foreground">
                                        {regenerateJob?.currentStep || t('Snags_regenerating_dzi', 'Regenerating...')}
                                        {regenerateJob?.progress ? ` ${regenerateJob.progress}%` : ''}
                                      </small>
                                    </div>
                                  ) : (
                                    <>
                                      {/* Upload Blueprint */}
                                      {canAdmin && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0"
                                          onClick={() => handleOpenUploadModal(floor)}
                                          title={t('Snags_upload_blueprint')}
                                        >
                                          <Upload className="h-4 w-4" />
                                        </Button>
                                      )}
                                      {/* Regenerate DZI - only show if has blueprint.
                                          Note: processBlueprintDzi has no permissions registry entry
                                          (backend gap) — left ungated per "no guessing" rule; see task-16 report. */}
                                      {floor.blueprintFile && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0 text-blue-500 hover:text-blue-600"
                                          onClick={() => handleRegenerateDzi(floor)}
                                          disabled={!!regeneratingFloorId}
                                          title={t('Snags_regenerate_dzi', 'Regenerate tiles (for offline download)')}
                                        >
                                          <RefreshCw className="h-4 w-4" />
                                        </Button>
                                      )}
                                      {/* Delete Blueprint - only show if has blueprint */}
                                      {floor.blueprintFile && canAdmin && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0 text-amber-500 hover:text-amber-600"
                                          onClick={() => handleDeleteBlueprint(floor)}
                                          disabled={deletingBlueprintFloorId === floor.id}
                                          title={t('Snags_delete_blueprint')}
                                        >
                                          {deletingBlueprintFloorId === floor.id ? (
                                            <LoaderCircle className="h-4 w-4 animate-spin" />
                                          ) : (
                                            <Image className="h-4 w-4" />
                                          )}
                                        </Button>
                                      )}
                                      {canAdmin && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0"
                                          onClick={() => handleEditFloor(floor, building.id)}
                                          title={t('Common_edit')}
                                        >
                                          <Pencil className="h-4 w-4" />
                                        </Button>
                                      )}
                                      {canAdmin && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                          onClick={() => handleDeleteFloor(floor)}
                                          title={t('Common_delete')}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </Collapse>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Settings - Side Column */}
        <div>
          {/* Infrastructure Types Card */}
          <Card className="mb-4">
            <CardHeader className="py-4">
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">{t('Snags_infrastructure_types')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder={t('Snags_add_infrastructure_type')}
                  value={newInfraType}
                  onChange={(e) => setNewInfraType(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddInfraType()}
                />
                <Button size="sm" className="shrink-0" onClick={handleAddInfraType}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {infrastructureTypes.length === 0 ? (
                <p className="text-muted-foreground text-center text-sm py-3 mb-0">
                  {t('Snags_no_infrastructure_types')}
                </p>
              ) : (
                <div className="space-y-1">
                  {infrastructureTypes.map((type, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center py-2 px-3 bg-muted rounded-md"
                    >
                      <span className="text-sm font-semibold">{type}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        onClick={() => handleRemoveInfraType(index)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <small className="text-muted-foreground block mt-3">
                {t('Snags_infrastructure_types_hint')}
              </small>

              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={handleResetInfraTypes}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {t('Snags_reset_infrastructure_types', 'Reset to defaults')}
              </Button>
            </CardContent>
          </Card>

          {/* Default Settings Card */}
          <Card className="mb-4">
            <CardHeader className="py-4">
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">{t('Snags_default_settings')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div>
                <Label className="text-sm font-semibold text-muted-foreground mb-2 block">{t('Snags_default_priority')}</Label>
                <Select value={defaultPriority} onValueChange={setDefaultPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">{t('Snags_priority_low')}</SelectItem>
                    <SelectItem value="MEDIUM">{t('Snags_priority_medium')}</SelectItem>
                    <SelectItem value="HIGH">{t('Snags_priority_high')}</SelectItem>
                    <SelectItem value="CRITICAL">{t('Snags_priority_critical')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>

      {/* Sticky save bar — appears only when the settings form has unsaved changes */}
      {canEdit && settingsDirty && (
        <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-6 flex items-center justify-between gap-3 border-t bg-background/95 px-6 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 rounded-b-lg">
          <span className="text-sm text-muted-foreground">
            {t('Snags_unsaved_changes', 'Unsaved changes')}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDiscardSettings}
              disabled={savingSettings}
            >
              <RotateCcw className="h-4 w-4" />
              {t('Common_discard', 'Discard')}
            </Button>
            <Button
              size="sm"
              onClick={handleSaveSettings}
              disabled={savingSettings || settingsLoading}
            >
              {savingSettings ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {savingSettings ? t('Common_saving') : t('Common_save_settings', 'Save Settings')}
            </Button>
          </div>
        </div>
      )}

      {/* Building Modal */}
      <Modal isOpen={showBuildingModal} toggle={() => setShowBuildingModal(false)}>
        <ModalHeader toggle={() => setShowBuildingModal(false)}>
          {editingBuilding ? t('Snags_edit_building') : t('Snags_add_building')}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>{t('Snags_building_name')} *</Label>
              <Input
                value={buildingForm.name}
                onChange={(e) => setBuildingForm({ ...buildingForm, name: e.target.value })}
                placeholder={t('Snags_building_name_placeholder')}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t('Snags_building_code')}</Label>
              <Input
                value={buildingForm.code}
                onChange={(e) => setBuildingForm({ ...buildingForm, code: e.target.value })}
                placeholder={t('Snags_building_code_placeholder')}
                maxLength={10}
              />
              <small className="text-muted-foreground">{t('Snags_building_code_hint')}</small>
            </FormGroup>
            <FormGroup>
              <Label>{t('Snags_building_description')}</Label>
              <Textarea
                value={buildingForm.description}
                onChange={(e) => setBuildingForm({ ...buildingForm, description: e.target.value })}
                placeholder={t('Snags_building_description_placeholder')}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowBuildingModal(false)}>
            {t('Common_cancel')}
          </Button>
          <Button onClick={handleSaveBuilding} disabled={creatingBuilding || updatingBuilding}>
            {(creatingBuilding || updatingBuilding) ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin" />
                {t('Common_saving')}
              </>
            ) : t('Common_save')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Floor Modal */}
      <Modal isOpen={showFloorModal} toggle={() => setShowFloorModal(false)}>
        <ModalHeader toggle={() => setShowFloorModal(false)}>
          {editingFloor ? t('Snags_edit_floor') : t('Snags_add_floor')}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>{t('Snags_floor_name')} *</Label>
              <Input
                value={floorForm.name}
                onChange={(e) => setFloorForm({ ...floorForm, name: e.target.value })}
                placeholder={t('Snags_floor_name_placeholder')}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t('Snags_floor_level')} *</Label>
              <Input
                type="number"
                value={floorForm.level}
                onChange={(e) => setFloorForm({ ...floorForm, level: e.target.value })}
                placeholder={t('Snags_floor_level_placeholder')}
              />
              <small className="text-muted-foreground">{t('Snags_floor_level_hint')}</small>
            </FormGroup>
            <FormGroup>
              <Label>{t('Snags_floor_description')}</Label>
              <Textarea
                value={floorForm.description}
                onChange={(e) => setFloorForm({ ...floorForm, description: e.target.value })}
                placeholder={t('Snags_floor_description_placeholder')}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowFloorModal(false)}>
            {t('Common_cancel')}
          </Button>
          <Button onClick={handleSaveFloor} disabled={creatingFloor || updatingFloor}>
            {(creatingFloor || updatingFloor) ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin" />
                {t('Common_saving')}
              </>
            ) : t('Common_save')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Blueprint Upload Modal */}
      {uploadModalFloorId && (
        <BlueprintUploadModal
          isOpen={showUploadModal}
          onClose={() => {
            setShowUploadModal(false)
            setUploadModalFloorId(null)
            setUploadModalFloorName(null)
          }}
          floorId={uploadModalFloorId}
          floorName={uploadModalFloorName || undefined}
          onSuccess={handleUploadModalSuccess}
        />
      )}
    </div>
  )
}

export default SnagsSettings
